import React, { useState } from 'react';
import { Activity, Zap, TrendingUp, Wifi, WifiOff, Database, Table, LineChart, RotateCw } from 'lucide-react';
import DashboardLayout from '../DashboardLayout';
import { useOptionChainStore } from '../../store/optionChainStore';
import { useSyncOptionChainStore } from '../../store/syncOptionChainStore';
import { useWebSocketStatusStore } from '../../contexts/LTPStore/useWebSocketStatusStore';
import ArbitrageView from './components/ArbitrageView';
import SyncArbitrageView from './components/SyncArbitrageView';
import StockScreenerView from './components/StockScreenerView';
import USOptionScreenerView from './components/USOptionScreenerView';
import IndiaOptionScreenerView from './components/IndiaOptionScreenerView';
import CMEOptionScreenerView from './components/CMEOptionScreenerView';
import CashCarryArbView from './components/CashCarryArbView';
import CommodityTrackerView from './components/CommodityTracker/CommodityTrackerView';

const OpportunityScreener = () => {
    const optionChainStatus = useOptionChainStore(state => state.status);
    const wsStatus = useWebSocketStatusStore(state => state.status);
    const [viewMode, setViewMode] = useState("arbitrage"); // 'arbitrage' | 'sync-arbitrage' | 'screener'
    const [activeTab, setActiveTab] = useState("GOLD");
    const [activeScreenerTab, setActiveScreenerTab] = useState("HPCHU/LPCLD Screener"); // 'HPCHU/LPCLD Screener' | 'US Stock Screener'

    const isCmeScreener = viewMode === "screener" && activeScreenerTab === "CME Screener";
    const isConnected = viewMode === "screener" || viewMode === "commodity-tracker"
        ? wsStatus === "open"
        : optionChainStatus === "connected";

    const handleRefresh = () => {
        useOptionChainStore.getState().reset();
        useSyncOptionChainStore.getState().reset();
    };

    return (
        <DashboardLayout>
            <div className="flex flex-col h-full w-full bg-[#0f172a] text-slate-200 font-inter selection:bg-indigo-500/30">
                {/* App Header */}
                <header className="flex-none px-6 py-4 flex justify-between items-center border-b border-slate-700/50 bg-[#0f172a]/95 backdrop-blur z-40">
                    <div className="flex items-center gap-4">
                        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-2 rounded-lg shadow-lg shadow-indigo-500/20">
                            <TrendingUp className="text-white w-5 h-5" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-slate-100 tracking-tight">Opportunity Screener</h1>
                            <p className="text-xs text-slate-500 font-medium">Global Volatility Monitor</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {(viewMode === 'arbitrage' || viewMode === 'sync-arbitrage') && (
                            <button
                                onClick={handleRefresh}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-700 bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 hover:text-white transition-colors text-xs font-bold tracking-wide outline-none cursor-pointer"
                                title="Refresh Arbitrage & Sync Arbitrage views"
                            >
                                <RotateCw size={14} />
                                <span>Refresh</span>
                            </button>
                        )}

                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${isCmeScreener
                            ? 'bg-slate-800 border-slate-700 text-slate-400'
                            : isConnected
                            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                            : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                            } `}>
                            {isCmeScreener ? <Database size={14} /> : isConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
                            <span className="text-xs font-bold tracking-wide">
                                {isCmeScreener ? "SAVED SNAPSHOT" : isConnected ? "CONNECTED" : "DISCONNECTED"}
                            </span>
                        </div>
                    </div>
                </header>

                {/* Section Tabs */}
                <div className="flex-none px-6 border-b border-slate-700/50 bg-[#0f172a]/95 backdrop-blur z-30">
                    <div className="flex flex-col xl:flex-row xl:items-center gap-x-8">
                        {/* VIEW MODE TABS */}
                        <div className="flex shrink-0 gap-4 py-4 xl:border-r border-slate-700/50 xl:pr-8 overflow-x-auto whitespace-nowrap">
                            <button
                                onClick={() => setViewMode("arbitrage")}
                                className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide transition-colors ${viewMode === 'arbitrage' ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'}`}
                            >
                                <Zap size={16} /> Arbitrage
                            </button>
                            <button
                                onClick={() => setViewMode("sync-arbitrage")}
                                className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide transition-colors ${viewMode === 'sync-arbitrage' ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'}`}
                            >
                                <Activity size={16} /> Sync Arb
                            </button>
                            <button
                                onClick={() => setViewMode("screener")}
                                className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide transition-colors ${viewMode === 'screener' ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'}`}
                            >
                                <Table size={16} /> Stock Screener
                            </button>
                            <button
                                onClick={() => setViewMode("commodity-tracker")}
                                className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide transition-colors ${viewMode === 'commodity-tracker' ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'}`}
                            >
                                <LineChart size={16} /> Commodity Tracker
                            </button>
                        </div>

                        {/* SUB TABS (Visible for Arbitrage & Sync Arb) */}
                        {(viewMode === 'arbitrage' || viewMode === 'sync-arbitrage') && (
                            <div className="flex gap-1 min-w-0 overflow-x-auto">
                                {['GOLD', 'SILVER', 'CRUDEOIL', 'NATURALGAS'].map((tab) => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`relative shrink-0 whitespace-nowrap px-4 py-4 text-xs font-bold uppercase tracking-wider transition-colors outline-none
                                            ${activeTab === tab
                                                ? 'text-indigo-400'
                                                : 'text-slate-500 hover:text-slate-300'
                                            }`}
                                    >
                                        {tab}
                                        {activeTab === tab && (
                                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]"></div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* SUB TABS for Stock Screener */}
                        {viewMode === 'screener' && (
                            <div className="flex gap-1 min-w-0 overflow-x-auto">
                                {['HPCHU/LPCLD Screener', 'US Stock Screener', 'India Stock Screener', 'MCX Commodity Screener', 'CME Screener', 'INTER ASSET ARB'].map((tab) => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveScreenerTab(tab)}
                                        className={`relative shrink-0 whitespace-nowrap px-4 py-4 text-xs font-bold uppercase tracking-wider transition-colors outline-none
                                            ${activeScreenerTab === tab
                                                ? 'text-indigo-400'
                                                : 'text-slate-500 hover:text-slate-300'
                                            }`}
                                    >
                                        {tab}
                                        {activeScreenerTab === tab && (
                                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]"></div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content Area */}
                <main className="flex-1 p-6 overflow-auto custom-scrollbar relative">
                    {viewMode === 'arbitrage' && (
                        <ArbitrageView
                            activeCommodityTab={activeTab}
                            setActiveCommodityTab={setActiveTab}
                        />
                    )}
                    {viewMode === 'sync-arbitrage' && (
                        <SyncArbitrageView
                            activeCommodityTab={activeTab}
                            setActiveCommodityTab={setActiveTab}
                        />
                    )}
                    {viewMode === 'screener' && activeScreenerTab === 'HPCHU/LPCLD Screener' && <StockScreenerView />}
                    {viewMode === 'screener' && activeScreenerTab === 'US Stock Screener' && <USOptionScreenerView />}
                    {viewMode === 'screener' && activeScreenerTab === 'India Stock Screener' && <IndiaOptionScreenerView bucket="nse_equity" />}
                    {viewMode === 'screener' && activeScreenerTab === 'MCX Commodity Screener' && <IndiaOptionScreenerView bucket="mcx_commodity" />}
                    {viewMode === 'screener' && activeScreenerTab === 'CME Screener' && <CMEOptionScreenerView />}
                    {viewMode === 'screener' && activeScreenerTab === 'INTER ASSET ARB' && <CashCarryArbView />}
                    {viewMode === 'commodity-tracker' && <CommodityTrackerView />}
                </main>
            </div>
        </DashboardLayout>
    );
};

export default OpportunityScreener;
